/**
 * API Request Metrics Utility
 * 
 * Publishes API request latency by viewed page, API route, and response status to DataDog RUM
 */

import { onDatadogRumReady } from '@/lib/analytics/datadogRum'

export interface ApiMetrics {
  requestMethod: string
  requestPath: string
  requestRoute?: string
  responseStatus: number
  errorName: string
  latencyMs: number
}

/**
 * Regex pattern to match UUIDs (case-insensitive)
 * Format: 8-4-4-4-12 hexadecimal digits
 */
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * Regex pattern to match numeric IDs (purely numeric path segments)
 */
const NUMERIC_ID_PATTERN = /^\d+$/

/**
 * Checks if a path segment is a resource ID (UUID or numeric)
 * 
 * @param segment - A single path segment
 * @returns True if the segment appears to be a resource ID
 */
function isResourceId(segment: string): boolean {
  return UUID_PATTERN.test(segment) || NUMERIC_ID_PATTERN.test(segment)
}

/**
 * Normalizes a request path by automatically replacing resource IDs with placeholders.
 * Resource IDs are detected as either UUIDs or numeric path segments.
 * Query parameters are stripped from the path before normalization.
 * 
 * @param requestPath - The raw request path (e.g., "/api/external/sku_options/400013?foo=bar")
 * @returns The normalized path pattern (e.g., "/api/external/sku_options/{id}")
 */
export function normalizeRequestPath(requestPath: string): string {
  // Strip query parameters (everything after '?')
  const pathWithoutQuery = requestPath.split('?')[0]
  
  // Split path into segments
  const segments = pathWithoutQuery.split('/').filter(segment => segment !== '')
  
  // Normalize each segment by replacing IDs with {id}
  const normalizedSegments = segments.map(segment => {
    if (isResourceId(segment)) {
      return '{id}'
    }
    return segment
  })
  
  // Reconstruct the path with leading slash
  const normalizedPath = normalizedSegments.length > 0 
    ? `/${normalizedSegments.join('/')}` 
    : '/'
  
  return normalizedPath
}

/**
 * Sends API request metrics to DataDog RUM
 * 
 * @param metrics - The API request metrics to track
 */
export function trackApiMetrics(metrics: ApiMetrics): void {
  // Only track on the client side
  if (typeof window === 'undefined' || !window.DD_RUM) {
    return
  }

  try {
    onDatadogRumReady((datadogRum) => {
      metrics.requestMethod = metrics.requestMethod.toUpperCase()
      metrics.requestPath = normalizeRequestPath(metrics.requestPath)
      metrics.requestRoute = `${metrics.requestMethod}/${metrics.requestPath}`
      datadogRum.addAction('nile-core-api-request', metrics)
    })
  } catch (error) {
    console.error('Failed to track API metrics:', error)
  }
}
